import { useEffect, useState } from 'react';

import type { DealPickerResult } from '../../hooks/useDeals';
import { useDealPickerSearch } from '../../hooks/useDeals';
import { useBindReview } from '../../hooks/useNda';

/**
 * Bind an NDA review to a deal via the SAME picker the Deal-Updates flow uses
 * (`useDealPickerSearch` → /api/deal-cards/search, unchanged). It reuses the
 * RefuteEvidenceModal *row pattern* — NOT MergeIntoDealModal's body, whose
 * source-deal exclusion + merge copy don't apply (a review has no "source
 * deal"). Inline styles keep this free of any DealsPage CSS-module coupling.
 *
 * Slice 3 of the NDA forward-binding work. The bind WRITE is server-gated to
 * nda_privileged_emails; this modal just drives the picker + mutation.
 */

/** Local debounce (DealsPage's useDebounced is not exported). */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function NdaBindModal({
  reviewId,
  onClose,
}: {
  reviewId: number;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<DealPickerResult | null>(null);
  const debounced = useDebounced(query, 200);
  // Stop searching once a pick is in flight.
  const search = useDealPickerSearch(debounced, picked === null);
  const bind = useBindReview();

  const handlePick = (r: DealPickerResult) => {
    setPicked(r);
    bind.mutate(
      { id: reviewId, dealKind: r.kind, dealKey: r.deal_key },
      { onSuccess: () => onClose(), onError: () => setPicked(null) },
    );
  };

  const results = search.data;
  const noMatches =
    results != null &&
    results.candidates.length === 0 &&
    results.affinity_opps.length === 0;

  return (
    <div
      style={backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={modal} role="dialog" aria-label="Bind review to a deal">
        <h3 style={{ marginTop: 0, color: '#333' }}>Bind to a deal</h3>
        <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
          Search Affinity opportunities and candidate deals. The same picker the
          Deal&nbsp;Updates flow uses.
        </p>

        <input
          type="text"
          style={input}
          placeholder="Search by codename / company / opp id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          disabled={bind.isPending}
        />

        {bind.isPending && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
            Binding{picked ? ` to ${picked.label}` : ''}…
          </div>
        )}
        {bind.isError && !bind.isPending && (
          <div style={{ fontSize: 12, color: '#B00020', marginTop: 6 }}>
            Could not bind — the deal may no longer exist. Try another.
          </div>
        )}

        {search.isLoading && debounced && !bind.isPending && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>Searching…</div>
        )}

        {results && !bind.isPending && (
          <div style={resultsBox}>
            {noMatches && (
              <div style={{ padding: 10, fontSize: 12, color: '#888' }}>
                No matches. Try a codename, company name, or paste an opp id.
              </div>
            )}
            {results.candidates.map((r) => (
              <div
                key={`cand-${r.deal_key}`}
                style={row}
                onClick={() => handlePick(r)}
              >
                <span>
                  <strong>{r.label}</strong>
                  {r.sublabel && <span style={{ color: '#888' }}> · {r.sublabel}</span>}
                </span>
                <span style={{ color: '#888', fontSize: 11 }}>candidate</span>
              </div>
            ))}
            {results.affinity_opps.map((r) => (
              <div
                key={`opp-${r.deal_key}`}
                style={row}
                onClick={() => handlePick(r)}
              >
                <span>
                  <strong>{r.label}</strong>
                  {r.stage && <span style={{ color: '#888' }}> · {r.stage}</span>}
                </span>
                <span style={{ color: '#888', fontSize: 11 }}>opp {r.deal_key}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button type="button" onClick={onClose} style={cancelBtn} disabled={bind.isPending}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── inline styles (no cross-module CSS coupling) ──────────────────────────────

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '12vh',
  zIndex: 1000,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: 20,
  width: 'min(520px, 92vw)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};

const input: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid #ccc',
  borderRadius: 4,
  boxSizing: 'border-box',
};

const resultsBox: React.CSSProperties = {
  marginTop: 8,
  maxHeight: 280,
  overflowY: 'auto',
  border: '1px solid #eee',
  borderRadius: 4,
};

const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  fontSize: 13,
  cursor: 'pointer',
  borderBottom: '1px solid #f2f2f2',
};

const cancelBtn: React.CSSProperties = {
  background: '#f2f2f2',
  border: '1px solid #ddd',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};
